import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Search,
  Download,
  RefreshCw,
  XCircle,
} from "lucide-react";

import {
  Money,
  OverdueChip,
  PaymentCardShell,
  PaymentEmptyState,
  PaymentListSkeleton,
  QuietChip,
  StatusChip,
  StatusWell,
  KpiTile,
} from "@/components/payments/PaymentBits";
import { usePaymentLabels, type PaymentLabels } from "@/lib/paymentLabels";
import {
  OVERDUE_TONE,
  WAITING_TONE,
  isOverdue,
  isSettled,
  tileStyle,
} from "@/lib/paymentStyles";

import {
  getInvoices,
  getBCVRate,
  type Invoice,
} from "@/api/payments";
import { listBuildings, type BuildingList as Condo } from "@/api/buildings";

/** Every invoice status, in lifecycle order, for the filter dropdown. */
const INVOICE_STATUSES = [
  "DRAFT",
  "SENT",
  "UNPAID",
  "PARTIALLY_PAID",
  "PENDING_VERIFICATION",
  "PENDING_HOA_TRANSFER",
  "PAID",
  "CANCELLED",
] as const;

type RangeKey = "1w" | "1m" | "3m" | "all";

const DAY = 24 * 60 * 60 * 1000;
function isWithinRange(dateStr: string, range: RangeKey, now = new Date()) {
  if (range === "all") return true;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return true;
  if (range === "1w") return d >= new Date(now.getTime() - 7 * DAY) && d <= now;
  if (range === "1m") return d >= new Date(now.getTime() - 30 * DAY) && d <= now;
  if (range === "3m") return d >= new Date(now.getTime() - 90 * DAY) && d <= now;
  return true;
}

function csvCell(value: unknown): string {
  return JSON.stringify(value ?? "");
}

function formatCsvDate(value?: string | null): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("es-VE");
}

function paymentMethodEs(method?: string | null): string {
  if (!method) return "";
  const map: Record<string, string> = {
    BANK_TRANSFER: "Transferencia bancaria",
    ZELLE: "Zelle",
    STRIPE: "Tarjeta",
    PAGO_MOVIL: "Pago Movil",
    CASH: "Efectivo",
  };
  return map[method] ?? method;
}

function invoiceStatusEs(status?: string | null): string {
  if (!status) return "";
  const map: Record<string, string> = {
    DRAFT: "Borrador",
    SENT: "Enviada",
    UNPAID: "No pagada",
    PENDING_VERIFICATION: "Pendiente de verificacion",
    PENDING_HOA_TRANSFER: "Pendiente de envio a HOA",
    PAID: "Pagada",
    CANCELLED: "Cancelada",
  };
  return map[status] ?? status;
}

function exportCsv(filename: string, rows: Invoice[]) {
  const headers = [
    "ID factura",
    "Periodo",
    "Edificio",
    "Unidad",
    "Residente",
    "Correo residente",
    "Monto total (USD)",
    "Metodo de pago",
    "Estado",
    "Creada",
    "Pago reportado",
    "Verificada",
  ];

  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      [
        r.id,
        r.period,
        r.building_name,
        r.unit_code,
        r.resident_name,
        r.resident_email,
        r.total_amount,
        paymentMethodEs(r.payment_method),
        invoiceStatusEs(r.status),
        formatCsvDate(r.created_at),
        formatCsvDate(r.marked_paid_at),
        formatCsvDate(r.verified_at),
      ]
        .map(csvCell)
        .join(",")
    ),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function PaymentsInboxPage() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const labels = usePaymentLabels();
  const dateLocale = language === "es" ? "es-ES" : "en-US";

  const [buildingFilter, setBuildingFilter] = useState<string>("ALL");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  // Overdue is not a status -- it is derived from the due date, and an
  // invoice can be overdue while SENT, UNPAID or PARTIALLY_PAID. It needs its
  // own dimension or the tile could only ever filter to one of the three.
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [q, setQ] = useState("");
  const [range, setRange] = useState<RangeKey>("all");

  const { data: bcvRateData } = useQuery({
    queryKey: ["bcv-rate"],
    queryFn: () => getBCVRate(),
    staleTime: 6 * 60 * 60 * 1000,
  });
  const bcvRate = bcvRateData?.dollar;

  const {
    data: allInvoices = [],
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["sa-all-invoices"],
    queryFn: getInvoices,
    refetchInterval: 60000,
  });

  const { data: condos = [] } = useQuery<Condo[]>({
    queryKey: ["buildings"],
    queryFn: listBuildings,
  });

  const filtered = useMemo(() => {
    const text = q.trim().toLowerCase();
    let list = allInvoices;

    if (buildingFilter !== "ALL") {
      list = list.filter((inv) => inv.building_id === buildingFilter);
    }
    if (statusFilter !== "ALL") {
      list = list.filter((inv) => inv.status === statusFilter);
    }
    if (overdueOnly) {
      list = list.filter((inv) => isOverdue(inv.status, inv.due_date, inv.is_overdue));
    }
    list = list.filter((inv) => isWithinRange(inv.created_at, range));
    if (text) {
      list = list.filter((inv) =>
        [
          inv.resident_name,
          inv.resident_email,
          inv.unit_code,
          inv.building_name,
          inv.payment_method || "",
        ]
          .join(" ")
          .toLowerCase()
          .includes(text)
      );
    }
    return [...list].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [allInvoices, buildingFilter, statusFilter, overdueOnly, q, range]);

  const stats = useMemo(() => {
    const total = allInvoices.length;
    const pendingVerification = allInvoices.filter(
      (i) => i.status === "PENDING_VERIFICATION"
    ).length;
    const paid = allInvoices.filter((i) => i.status === "PAID").length;
    // Was `(UNPAID || SENT) && is_overdue`, which silently excluded every
    // PARTIALLY_PAID invoice -- a resident who paid half and then went late
    // was counted as fine. isOverdue() owns the rule for the whole app.
    const overdue = allInvoices.filter((i) =>
      isOverdue(i.status, i.due_date, i.is_overdue)
    ).length;
    return { total, pendingVerification, paid, overdue };
  }, [allInvoices]);

  const narrowed =
    statusFilter !== "ALL" || buildingFilter !== "ALL" || overdueOnly || range !== "all" || q !== "";

  function resetFilters() {
    setQ("");
    setRange("all");
    setStatusFilter("ALL");
    setBuildingFilter("ALL");
    setOverdueOnly(false);
  }

  /** Every tile clears the others: they are alternative views, not layers. */
  function only(next: { status?: string; overdue?: boolean }) {
    setStatusFilter(next.status ?? "ALL");
    setOverdueOnly(next.overdue ?? false);
  }

  if (isLoading && allInvoices.length === 0) {
    return (
      <div className="space-y-5">
        <PaymentListSkeleton rows={6} />
      </div>
    );
  }

  return (
    <div className="w-full max-w-full space-y-5 overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-[19px] font-semibold tracking-[-0.01em] text-gray-900 sm:text-[21px]">
            {t("superAdmin.payments.title")}
          </h1>
          <p className="mt-0.5 text-[13px] text-gray-500">
            {t("superAdmin.payments.subtitle")}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            aria-label={t("superAdmin.common.refresh")}
            title={t("superAdmin.common.refresh")}
            className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white text-gray-600 ring-1 ring-gray-200 transition hover:bg-gray-50 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93]"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={() => exportCsv("monitoreo-pagos.csv", filtered)}
            className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-xl bg-white px-3 text-sm font-medium text-gray-700 ring-1 ring-gray-200 transition hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93]"
          >
            <Download className="h-4 w-4" aria-hidden />
            <span className="hidden sm:inline">{t("superAdmin.payouts.exportCsv")}</span>
          </button>
        </div>
      </div>

      {/* Tiles. Every one of these FILTERS -- they were four decorative
          numbers, identical in appearance to nothing you could click, above a
          toolbar where you then re-entered the same thing by hand. */}
      <div className="-mx-4 flex snap-x gap-2.5 overflow-x-auto px-4 pb-1 sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0 lg:grid-cols-4">
        <KpiTile
          label={t("superAdmin.payments.tabs.all")}
          value={stats.total}
          tone={tileStyle(WAITING_TONE.NOBODY)}
          active={statusFilter === "ALL" && !overdueOnly}
          onClick={() => only({})}
        />
        <KpiTile
          label={t("superAdmin.payments.tabs.review")}
          value={stats.pendingVerification}
          sub={t("hoa.payments.kpi.needsReview")}
          tone={tileStyle(WAITING_TONE.YOU)}
          active={statusFilter === "PENDING_VERIFICATION"}
          onClick={() =>
            only(
              statusFilter === "PENDING_VERIFICATION"
                ? {}
                : { status: "PENDING_VERIFICATION" }
            )
          }
        />
        <KpiTile
          label={t("superAdmin.payments.filters.verifiedPaid")}
          value={stats.paid}
          tone={tileStyle(WAITING_TONE.SETTLED)}
          active={statusFilter === "PAID"}
          onClick={() => only(statusFilter === "PAID" ? {} : { status: "PAID" })}
        />
        <KpiTile
          label={t("resident.dashboard.overdue")}
          value={stats.overdue}
          tone={tileStyle(stats.overdue > 0 ? OVERDUE_TONE : WAITING_TONE.NOBODY)}
          active={overdueOnly}
          onClick={() => only(overdueOnly ? {} : { overdue: true })}
        />
      </div>

      {/* Toolbar */}
      <div className="space-y-2.5">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            placeholder={t("superAdmin.payments.toolbar.search")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-10 w-full rounded-xl bg-white pl-9 pr-9 text-sm text-gray-900 ring-1 ring-gray-200 transition placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1F3A93]"
          />
          {q && (
            <button
              onClick={() => setQ("")}
              aria-label={t("superAdmin.payments.actions.reset")}
              className="absolute right-2 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <XCircle className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          <InboxSelect
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v);
              // Two independent dimensions can otherwise combine into an
              // empty list with nothing on screen saying why.
              setOverdueOnly(false);
            }}
            options={[
              { value: "ALL", label: t("superAdmin.payments.filters.allStatus") },
              ...INVOICE_STATUSES.map((s) => ({ value: s, label: labels.status[s] })),
            ]}
          />
          <InboxSelect
            value={buildingFilter}
            onChange={setBuildingFilter}
            options={[
              { value: "ALL", label: t("superAdmin.payments.toolbar.allBuildings") },
              ...condos.map((c) => ({ value: c.id, label: c.name })),
            ]}
          />
          <InboxSelect
            value={range}
            onChange={(v) => setRange(v as RangeKey)}
            className="col-span-2 sm:col-span-1"
            options={[
              { value: "all", label: t("superAdmin.payments.filters.allTime") },
              { value: "1w", label: t("superAdmin.payments.filters.lastWeek") },
              { value: "1m", label: t("superAdmin.payments.filters.lastMonth") },
              { value: "3m", label: t("superAdmin.payments.filters.last3Months") },
            ]}
          />
        </div>

        <div className="flex items-center justify-between gap-3">
          <span className="text-xs tabular-nums text-gray-500">
            {t("superAdmin.payments.toolbar.showingCount", { count: filtered.length })}
          </span>
          {narrowed && (
            <button
              onClick={resetFilters}
              className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 underline-offset-2 hover:text-gray-800 hover:underline"
            >
              <XCircle className="h-3 w-3" />
              {t("superAdmin.payments.actions.reset")}
            </button>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <PaymentEmptyState
          title={t("superAdmin.payments.states.noResults")}
          hint={narrowed ? t("hoa.payments.noResultsFiltered") : undefined}
          action={
            narrowed ? (
              <button
                onClick={resetFilters}
                className="rounded-xl bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50"
              >
                {t("superAdmin.payments.actions.reset")}
              </button>
            ) : undefined
          }
        />
      ) : (
        <>
          {/* Mobile */}
          <div className="space-y-2.5 md:hidden">
            {filtered.map((inv) => (
              <InboxCard
                key={inv.id}
                inv={inv}
                labels={labels}
                bcvRate={bcvRate}
                dateLocale={dateLocale}
                onOpen={() => navigate(`/super-admin/payments/${inv.id}`)}
              />
            ))}
          </div>

          {/* Desktop */}
          <div className="hidden overflow-hidden rounded-3xl bg-white/80 ring-1 ring-black/5 shadow-sm shadow-black/[0.03] backdrop-blur-xl md:block">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50/80">
                  <tr className="text-left text-[11px] font-bold uppercase tracking-[0.06em] text-gray-500">
                    <th className="px-4 py-3">{t("superAdmin.payments.table.resident")}</th>
                    <th className="px-4 py-3">{t("superAdmin.payments.table.building")}</th>
                    <th className="px-4 py-3 text-right">{t("superAdmin.payments.table.amount")}</th>
                    <th className="px-4 py-3">{t("superAdmin.payments.table.method")}</th>
                    <th className="px-4 py-3">{t("superAdmin.payments.table.date")}</th>
                    <th className="px-4 py-3">{t("superAdmin.payments.table.status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((inv) => {
                    const overdue = isOverdue(inv.status, inv.due_date, inv.is_overdue);
                    return (
                      /* The whole row opens it. A 28px "Details" button was
                         the only target on a row 1200px wide. */
                      <tr
                        key={inv.id}
                        onClick={() => navigate(`/super-admin/payments/${inv.id}`)}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            navigate(`/super-admin/payments/${inv.id}`);
                          }
                        }}
                        className={`cursor-pointer border-t border-black/5 transition hover:bg-gray-50/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#1F3A93] ${
                          isSettled(inv.status) ? "opacity-[0.72] hover:opacity-100" : ""
                        }`}
                      >
                        <td className="px-4 py-3">
                          <span className="block font-medium text-gray-900">
                            {inv.resident_name}
                          </span>
                          <span className="block text-[11px] text-gray-500">
                            {inv.unit_code} · {fmtPeriodShort(inv.period, dateLocale)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {inv.building_name || t("superAdmin.payments.states.unknownBuilding")}
                        </td>
                        <td className="px-4 py-3">
                          <Money
                            usd={inv.total_amount}
                            ves={
                              rateFor(inv, bcvRate)
                                ? Number(inv.total_amount) * rateFor(inv, bcvRate)!
                                : null
                            }
                          />
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-gray-600">
                          {inv.payment_method ? (
                            <QuietChip>{inv.payment_method}</QuietChip>
                          ) : (
                            <span className="text-gray-400">{t("common.na")}</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-gray-600">
                          {new Date(inv.created_at).toLocaleDateString(dateLocale)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <StatusChip status={inv.status} labels={labels} size="sm" />
                            {overdue && <OverdueChip />}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** The BCV rate that applies to this invoice: its own snapshot, else today's. */
function rateFor(inv: Invoice, fallback?: number): number | undefined {
  return inv.bcv_rate_snapshot ? parseFloat(inv.bcv_rate_snapshot) : fallback;
}

/** "2026-08" -> "Aug 2026". A bare period reads as a product code. */
function fmtPeriodShort(period?: string, locale = "en-US") {
  if (!period) return "";
  const [y, m] = period.split("-").map(Number);
  if (!y || !m) return period;
  return new Date(y, m - 1, 1).toLocaleDateString(locale, {
    month: "short",
    year: "numeric",
  });
}

function InboxSelect({
  value,
  onChange,
  options,
  className = "",
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  className?: string;
}) {
  const isAll = value === "ALL" || value === "all";
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`h-10 w-full rounded-xl px-3 text-sm ring-1 transition focus:outline-none focus:ring-2 focus:ring-[#1F3A93] ${
        isAll
          ? "bg-white text-gray-700 ring-gray-200"
          : "bg-[#E7EBF8] font-medium text-[#1B3480] ring-[#C3CDEC]"
      } ${className}`}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function InboxCard({
  inv,
  labels,
  bcvRate,
  dateLocale,
  onOpen,
}: {
  inv: Invoice;
  labels: PaymentLabels;
  bcvRate?: number;
  dateLocale: string;
  onOpen: () => void;
}) {
  const { t } = useLanguage();
  const overdue = isOverdue(inv.status, inv.due_date, inv.is_overdue);
  const rate = rateFor(inv, bcvRate);

  return (
    <PaymentCardShell status={inv.status} overdue={overdue} settled={isSettled(inv.status)}>
      <button
        onClick={onOpen}
        className="flex w-full items-start gap-3 px-3.5 py-3.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#1F3A93]"
      >
        <StatusWell status={inv.status} overdue={overdue} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="min-w-0 truncate text-[15px] font-semibold text-gray-900">
              {inv.resident_name}
            </h3>
            <Money usd={inv.total_amount} ves={rate ? Number(inv.total_amount) * rate : null} />
          </div>
          <p className="mt-0.5 truncate text-[13px] text-gray-600">
            {inv.building_name || t("superAdmin.payments.states.unknownBuilding")} · {inv.unit_code}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5">
            <StatusChip status={inv.status} labels={labels} size="sm" />
            {overdue && <OverdueChip />}
            {inv.payment_method && <QuietChip>{inv.payment_method}</QuietChip>}
          </div>
          <p className="mt-2 text-[11px] text-gray-500">
            {new Date(inv.created_at).toLocaleDateString(dateLocale)}
          </p>
        </div>
      </button>
    </PaymentCardShell>
  );
}
